import { useEffect, useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import CloseOutlined from "@mui/icons-material/CloseOutlined";
import {
  createDiagramaTemplate,
  updateDiagramaTemplate,
} from "../templatesService";
import { sanitizeDiagramForTemplate } from "../templateUtils";
import styles from "./styles";

// Modal de salvar/renomear template: em "create" captura o diagrama atual sanitizado;
// em "update" edita apenas nome e descrição (dados do template nunca mudam por aqui)
export default function ModalSalvarTemplate({
  open = false,
  onClose = () => {},
  onSaved = () => {},
  mode = "create",
  initialData = null,
  diagramData = null,
  setSnackbar = () => {},
}) {
  const theme = useTheme();
  const modalStyles = styles(theme);
  const [nome, setNome] = useState("");
  const [descricao, setDescricao] = useState("");
  const [saving, setSaving] = useState(false);

  // Reseta o formulário a cada abertura conforme o modo (criar/editar)
  useEffect(() => {
    if (!open) return;
    setNome(initialData?.nome || "");
    setDescricao(initialData?.descricao || "");
    setSaving(false);
  }, [open, initialData]);

  const nomeValido = !!nome?.trim?.();
  const podeSalvar =
    nomeValido &&
    !saving &&
    (mode !== "create" || (diagramData?.nodes?.length || 0) > 0);

  // Salva imediatamente (fora do lote de save do circuito), igual ao equipamento personalizado
  const handleSave = async () => {
    if (!podeSalvar) return;
    setSaving(true);
    try {
      if (mode === "create") {
        const dados = sanitizeDiagramForTemplate(
          diagramData?.nodes,
          diagramData?.edges,
        );
        await createDiagramaTemplate({
          nome: nome?.trim?.(),
          descricao: descricao?.trim?.() || null,
          dados,
        });
      } else {
        await updateDiagramaTemplate({
          id: initialData?.id,
          nome: nome?.trim?.(),
          descricao: descricao?.trim?.() || null,
        });
      }
      onSaved?.();
      onClose?.();
    } catch (error) {
      console.error("Erro ao salvar template:", error);
      setSnackbar?.({
        children: "Erro ao salvar template.",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ style: { borderRadius: 16 } }}
    >
      <DialogTitle sx={modalStyles?.modalTitle}>
        <Typography variant="h6" fontWeight="bold">
          {mode === "create" ? "Salvar como template" : "Editar template"}
        </Typography>
        <IconButton onClick={onClose} size="small" aria-label="Fechar">
          <CloseOutlined fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <TextField
          label="Nome"
          fullWidth
          autoComplete="off"
          value={nome}
          onChange={(evento) => setNome(evento?.target?.value || "")}
          helperText={nomeValido ? "" : "Informe um nome"}
          sx={modalStyles?.nomeField}
        />
        <TextField
          label="Descrição (opcional)"
          fullWidth
          multiline
          minRows={2}
          autoComplete="off"
          value={descricao}
          onChange={(evento) => setDescricao(evento?.target?.value || "")}
          sx={modalStyles?.descricaoField}
        />
        {mode === "create" && (
          <Typography sx={modalStyles?.resumoHint}>
            {`${diagramData?.nodes?.length || 0} equipamento(s) e ${
              diagramData?.edges?.length || 0
            } conexão(ões) serão salvos. IPs e anexos não são incluídos, mas o nome e as observações digitados em cada equipamento ficam salvos no template e serão vistos por todos os operadores que usarem esse template em qualquer cliente.`}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button variant="outlined" onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={!podeSalvar}>
          {saving ? <CircularProgress size={22} /> : "Salvar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
